import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, Link } from 'react-router-dom'

import sidebarMenu from './menu'

import logo from '../../assets/images/Logo.png'
import avatar from '../../assets/images/avatar.png'
import merchantApi from '../../services/merchantApi'
import { Icon } from '@iconify/react'
import { Avatar } from '@mui/material'

// ─── Outlet Switcher Modal ────────────────────────────────────────────────────

function OutletSwitcherModal({ outletList, currentOutlet, onSelect, onClose, isLoading }) {
  const [search, setSearch] = useState('')

  const filtered = outletList.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    o.address.toLowerCase().includes(search.toLowerCase())
  )

  return createPortal(
    <div className="outlet-modal-overlay" onClick={onClose}>
      <div className="outlet-modal" onClick={(e) => e.stopPropagation()}>

        <div className="outlet-modal__header">
          <div>
            <h2 className="outlet-modal__title">Switch outlet</h2>
            <p className="outlet-modal__subtitle">Select an outlet to manage</p>
          </div>
          <button className="outlet-modal__close" onClick={onClose} disabled={isLoading}>
            <Icon icon="ph:x" height={20} />
          </button>
        </div>

        <div className="outlet-modal__search">
          <Icon icon="ph:magnifying-glass" height={18} className="outlet-modal__search-icon" />
          <input
            type="text"
            placeholder="Search outlets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            disabled={isLoading}
          />
        </div>

        <div className="outlet-modal__list">
          {isLoading && (
            <p className="outlet-modal__empty">Switching outlet...</p>
          )}
          {!isLoading && filtered.length === 0 && (
            <p className="outlet-modal__empty">No outlets found</p>
          )}
          {!isLoading && filtered.map((outlet) => {
            const isActive = outlet.id === currentOutlet?.id
            const isClosed = outlet?.status == 0
            return (
              <button
                key={outlet.id}
                className={`outlet-modal__item ${isActive ? 'outlet-modal__item--active' : ''} ${isClosed ? 'outlet-modal__item--closed' : ''}`}
                onClick={() => { onSelect(outlet) }}
                disabled={isLoading || isClosed}
              >
                <div className="outlet-modal__item-icon">
                  <Icon icon={`ph:map-pin${isActive ? '-fill' : ''}`} height={20} />
                </div>
                <div className="outlet-modal__item-info">
                  <p className="outlet-modal__item-name">{outlet?.name}</p>
                  <p className="outlet-modal__item-address">{outlet?.address}</p>
                </div>
                <div className="outlet-modal__item-right">
                  {isClosed
                    ? <span className="outlet-modal__item-closed-badge">Closed</span>
                    : isActive
                      ? <Icon icon="ph:check-circle-fill" height={20} className="outlet-modal__item-check"/>
                      : null
                  }
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const [currentOutlet, setCurrentOutlet] = useState()
  const [showOutletModal, setShowOutletModal] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [userInfo, setUserInfo] = useState(false)
  const [outletList, setOutletList] = useState([])
  const [switchingOutlet, setSwitchingOutlet] = useState(false)
  const [orderOpenNum, setOrderOpenNum] = useState(0)

  useEffect(() => {
    const loadData = async () => {
      try {
        const userInfoResponse = await merchantApi.auth.me();
        setUserInfo(userInfoResponse?.data || null)
        setCurrentOutlet(userInfoResponse?.data?.default_outlet || null)
        setOutletList(userInfoResponse?.data?.outlets || [])
      } catch (error) {
        console.log("Error fetching data:", error)
      }

    }
    loadData()
    loadGeneralInfo()
  }, [])

  const loadGeneralInfo = async () => {
    try {
      const response = await merchantApi.general.systemInfo();
      setOrderOpenNum(response?.data?.orders?.open_orders || 0)
    } catch (error) {
      console.log("Error fetching general info:", error)
    }
  }

  const handleOutletChange = async (outlet) => {
    if (!outlet || switchingOutlet) return
    setSwitchingOutlet(true)
    try {
      const response = await merchantApi.auth.switchOutlet({ outlet_id: outlet.id });
      setCurrentOutlet(response?.data?.default_outlet || outlet)
      setShowOutletModal(false)
      window.location.reload() // Reload the page to reflect the outlet change
    } catch (error) {
      console.log("Error switching outlet:", error)
    } finally {
      setSwitchingOutlet(false)
    }
  }

  const handleLogout = async () => {
    try {
      await merchantApi.auth.logout()
      window.location.href = '/login'
    } catch (error) {
      console.log("Error logging out:", error)
    }
  }
  
  return (
    <>
      <aside className={[
        'sidebar',
        sidebarOpen ? 'sidebar--open' : '',
        collapsed ? 'sidebar--collapsed' : '',
      ].filter(Boolean).join(' ')}>

        {/* ── HEADER ── */}
        <div className="sidebar__header">
          {!collapsed && (
            <>
              <img src={userInfo?.merchant?.logo_url || logo} alt="Cuscapi" className="sidebar__logo" />
              <span className="sidebar__merchant">{userInfo?.merchant?.business_name || 'Merchant'}</span>
            </>
          )}

          {/* Collapse toggle */}
          <button
            className="sidebar__collapse-btn"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed
              ? <Icon icon="ph:caret-double-right" height={16} />
              : <Icon icon="ph:caret-double-left" height={16} />
            }
          </button>
        </div>

        {/* ── OUTLET BUTTON ── */}
        <button
          className="sidebar__outlet"
          onClick={() => setShowOutletModal(true)}
          title={collapsed ? currentOutlet?.name : undefined}
          disabled={switchingOutlet}
        >
          
          {!collapsed && (
            <>
              <Icon icon="ph:circle-fill" height={9} className="sidebar__outlet-dot status--success" />
              <span className="sidebar__outlet-name">{switchingOutlet ? 'Switching...' : currentOutlet?.name || 'No outlet selected'}</span>
            </>
          )}
          {!collapsed && <Icon icon="ph:arrows-left-right" height={16} />}
          {collapsed && <Icon icon="ph:arrows-left-right" height={16} style={{color: '#52525B'}}/>}
        </button>

        {/* ── NAV ── */}
        <nav className="sidebar__nav">
          {sidebarMenu.map((group) => (
            <div className="sidebar__group" key={group.title}>
              {!collapsed && (
                <p className="sidebar__group-title">{group.title}</p>
              )}
              {collapsed && <div className="sidebar__group-divider" />}

              <div className="sidebar__menu">
                {group.items.map((item) => {
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={'sidebar__item sidebar__item--active'}
                      onClick={() => setSidebarOpen(false)}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon icon={item.icon} width={18} height={18} className="sidebar__item-icon" />
                      {!collapsed && (
                        <span className="sidebar__item-label">{item.label}</span>
                      )}
                      {Boolean(item.label === 'Orders' && orderOpenNum && !collapsed) && (
                        <span className="sidebar__badge badge badge--primary">{orderOpenNum}</span>
                      )}
                      {Boolean(item.label === 'Orders' && orderOpenNum && collapsed) && (
                        <span className="sidebar__badge sidebar__badge--dot" />
                      )}
                    </NavLink>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* ── FOOTER ── */}
        <div className="sidebar__footer">
          {!collapsed ? (
            <>
              <Link to="/profile" className="sidebar__profile">
                {
                  userInfo?.profile_photo_url ?
                    <img src={userInfo?.profile_photo_url || avatar} alt="User" className="sidebar__avatar" />
                    : <Avatar className="sr-table__mui-avatar" />
                }
                <span className="sidebar__name">{userInfo?.name || 'User'}</span>
              </Link>
              <div className="sidebar__logout" onClick={() => handleLogout()}>
                <Icon icon="ph:sign-out" height={16} />
                Logout
              </div>
            </>
          ) : (
            <>
              {
                userInfo?.profile_photo_url ?
                  <img src={userInfo?.profile_photo_url} alt="User" className="sidebar__avatar sidebar__avatar--center" title={userInfo?.name || 'User'} />
                  : <Avatar className="sr-table__mui-avatar" />
              }

              <button className="sidebar__logout sidebar__logout--icon" title="Logout">
                <Icon icon="ph:sign-out" height={16} />
              </button>
            </>
          )}
        </div>
      </aside>

      {showOutletModal && (
        <OutletSwitcherModal
          outletList={outletList}
          currentOutlet={currentOutlet}
          onSelect={handleOutletChange}
          onClose={() => setShowOutletModal(false)}
          isLoading={switchingOutlet}
        />
      )}
    </>
  )
}